/**
 * AdminAppfolioImport — Two-step CSV backfill.
 *
 *  Step 1: paste CSV URL → click "Preview" → backend returns 4 buckets
 *          (Jobs, Properties, Vendors, Residents) with per-row "would_create"
 *          flags.
 *  Step 2: cherry-pick — every row has a checkbox. Each bucket has a
 *          select-all toggle. Click "Import selected" → only checked items
 *          are created. Idempotent.
 */
import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadSimple, Check, ArrowRight, House, ClockCounterClockwise,
  Wrench, Users, Briefcase, MagnifyingGlass, ArrowsClockwise, Sparkle,
} from "@phosphor-icons/react";

const TABS = [
  { id: "jobs",       label: "Work Orders", icon: Briefcase, accent: "emerald" },
  { id: "properties", label: "Addresses",   icon: House,     accent: "cyan"    },
  { id: "vendors",    label: "Sub-Contractors", icon: Wrench, accent: "amber"  },
  { id: "residents",  label: "Tenants",     icon: Users,     accent: "fuchsia" },
];

const TONE = {
  emerald: { chip: "bg-emerald-100 text-emerald-800", btn: "bg-emerald-600 hover:bg-emerald-700", accent: "text-emerald-700" },
  cyan:    { chip: "bg-cyan-100 text-cyan-800",       btn: "bg-cyan-600 hover:bg-cyan-700",       accent: "text-cyan-700" },
  amber:   { chip: "bg-amber-100 text-amber-800",     btn: "bg-amber-600 hover:bg-amber-700",     accent: "text-amber-700" },
  fuchsia: { chip: "bg-fuchsia-100 text-fuchsia-800", btn: "bg-fuchsia-600 hover:bg-fuchsia-700", accent: "text-fuchsia-700" },
};

export default function AdminAppfolioImport() {
  const [csvUrl, setCsvUrl]     = useState("");
  const [stageToken, setStageToken] = useState("");
  const [stagedFile, setStagedFile] = useState(null); // {filename, rows}
  const [uploading, setUploading]   = useState(false);
  const [busy, setBusy]         = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [runs, setRuns]         = useState([]);
  const [tab, setTab]           = useState("jobs");
  const [query, setQuery]       = useState("");
  const [updateExisting, setUpdateExisting] = useState(false);

  // Selections keyed by bucket
  const [sel, setSel] = useState({
    jobs: new Set(),        // WO numbers (string)
    properties: new Set(),  // address_key
    vendors: new Set(),     // vendor display name
    residents: new Set(),   // resident display name
  });

  const loadHistory = async () => {
    try { setRuns((await api.get("/imports/appfolio/history")).data.runs || []); } catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const doPreview = async () => {
    const hasUpload = !!stageToken;
    if (!hasUpload && !csvUrl.trim()) { toast.error("Paste a CSV URL or upload a file"); return; }
    setBusy(true); setPreview(null); setQuery("");
    try {
      const payload = hasUpload ? { stage_token: stageToken } : { csv_url: csvUrl.trim() };
      const r = await api.post("/imports/appfolio/preview", payload);
      setPreview(r.data);
      // Default: pre-select every row that WOULD create AND — if "sync existing"
      // is on — every already-imported WO too, so re-imports are one tap.
      setSel({
        jobs:       new Set((r.data.jobs || [])
                            .filter(x => x.wo && (x.would_create || (updateExisting && x.skip_reason === "already_imported")))
                            .map(x => x.wo)),
        properties: new Set((r.data.properties || []).filter(x => x.would_create).map(x => x.address_key)),
        vendors:    new Set((r.data.vendors    || []).filter(x => x.would_create).map(x => x.name)),
        residents:  new Set((r.data.residents  || []).filter(x => x.would_create).map(x => x.name)),
      });
      const c = r.data.counts || {};
      toast.success(`Ready · ${c.jobs_new || 0} WOs · ${c.properties_new || 0} addresses · ${c.vendors_new || 0} subs · ${c.residents_new || 0} tenants pre-picked`);
      setTab("jobs");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Preview failed")); }
    setBusy(false);
  };

  const totalSelected = sel.jobs.size + sel.properties.size + sel.vendors.size + sel.residents.size;

  const doApplySelected = async () => {
    if (!preview || totalSelected === 0) {
      toast.error("Nothing selected — check at least one row"); return;
    }
    if (!window.confirm(
      `Import ${sel.jobs.size} WOs, ${sel.properties.size} addresses, ${sel.vendors.size} subs, and ${sel.residents.size} tenants?`,
    )) return;
    setApplying(true);
    try {
      const sourcePayload = stageToken ? { stage_token: stageToken } : { csv_url: csvUrl.trim() };
      const r = await api.post("/imports/appfolio/apply-selected", {
        ...sourcePayload,
        selected_wo_numbers:      Array.from(sel.jobs),
        selected_property_keys:   Array.from(sel.properties),
        selected_vendor_names:    Array.from(sel.vendors),
        selected_resident_names:  Array.from(sel.residents),
        update_existing:          updateExisting,
      });
      const d = r.data;
      toast.success(
        `Done · ${d.created_wos} new WOs · ${d.updated_wos || 0} synced · ${d.created_properties} addresses · ${d.created_vendors} subs · ${d.created_residents} tenants`,
        { duration: 6000 },
      );
      setPreview(null); setCsvUrl(""); setStageToken(""); setStagedFile(null);
      setSel({ jobs: new Set(), properties: new Set(), vendors: new Set(), residents: new Set() });
      await loadHistory();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed")); }
    setApplying(false);
  };

  const bucket = preview?.[tab] || [];
  const filtered = useMemo(() => {
    if (!query.trim()) return bucket;
    const q = query.trim().toLowerCase();
    return bucket.filter(item => JSON.stringify(item).toLowerCase().includes(q));
  }, [bucket, query]);

  const key = (item) => tab === "jobs" ? item.wo
                     : tab === "properties" ? item.address_key
                     : item.name;

  const toggle = (k) => {
    if (!k) return;
    setSel(prev => {
      const next = { ...prev, [tab]: new Set(prev[tab]) };
      if (next[tab].has(k)) next[tab].delete(k);
      else                  next[tab].add(k);
      return next;
    });
  };

  const setAllInFiltered = (checked) => {
    setSel(prev => {
      const next = { ...prev, [tab]: new Set(prev[tab]) };
      for (const it of filtered) {
        const k = key(it);
        if (!k) continue;
        if (tab === "jobs" && !it.would_create) {
          // Only bulk-add "exists" rows when sync mode is on
          if (!(updateExisting && it.skip_reason === "already_imported")) continue;
        }
        if (checked) next[tab].add(k);
        else         next[tab].delete(k);
      }
      return next;
    });
  };

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="appfolio-import-page">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700 flex items-center gap-1">
            <UploadSimple weight="fill" size={12}/> Data Import
          </div>
          <h1 className="font-display font-black text-3xl mt-1">Appfolio Backfill · Cherry-Pick</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Upload the CSV you exported from AppFolio (or paste a URL), preview the delta, then check off exactly what you want to bring in — work orders,
            addresses, sub-contractors, and tenants — all in one shot. Dedupe by WO # / normalized name.
          </p>
          <a href="/admin/email-sync-setup" data-testid="try-email-sync-link"
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-700 hover:to-fuchsia-700 text-white px-4 py-2 shadow-sm">
            <Sparkle weight="fill" size={14}/>
            <span className="text-[11px] font-black uppercase tracking-widest">Tired of re-importing? Try Email Sync →</span>
          </a>
        </div>

        {/* Step 1 */}
        <div className="rugged-card p-4 space-y-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <span className="h-5 w-5 inline-flex items-center justify-center bg-slate-900 text-white text-[10px]">1</span>
            Paste CSV URL <span className="text-slate-400 normal-case tracking-normal font-medium">or upload a file</span>
          </div>
          <input type="url" data-testid="csv-url-input"
            value={csvUrl}
            onChange={e => { setCsvUrl(e.target.value); if (e.target.value.trim()) { setStageToken(""); setStagedFile(null); } }}
            placeholder="https://.../work_order-20260815.csv"
            disabled={!!stagedFile}
            className="w-full h-11 px-3 border-2 border-slate-300 focus:border-cyan-500 font-mono text-xs outline-none disabled:bg-slate-100 disabled:text-slate-400"/>
          <div className="text-center text-[10px] font-black uppercase tracking-widest text-slate-400">— or —</div>
          <input
            type="file"
            accept=".csv,text/csv,application/vnd.ms-excel"
            id="appfolio-csv-file"
            data-testid="csv-file-input"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setUploading(true);
              try {
                const fd = new FormData();
                fd.append("file", f);
                const r = await api.post("/imports/appfolio/stage", fd,
                  { headers: { "Content-Type": "multipart/form-data" } });
                setStageToken(r.data.stage_token);
                setStagedFile({ filename: r.data.filename, rows: r.data.rows });
                setCsvUrl("");
                toast.success(`Uploaded ${r.data.filename} · ${r.data.rows} rows ready to preview`);
              } catch (err) {
                toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Upload failed"));
              } finally {
                setUploading(false);
                e.target.value = "";
              }
            }}/>
          {stagedFile ? (
            <div className="flex items-center justify-between gap-2 p-2 border-2 border-emerald-500 bg-emerald-50" data-testid="csv-staged-badge">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Uploaded</div>
                <div className="font-mono text-xs truncate text-slate-800">{stagedFile.filename} <span className="text-slate-500">· {stagedFile.rows} rows</span></div>
              </div>
              <button
                type="button"
                data-testid="csv-staged-clear"
                onClick={() => { setStageToken(""); setStagedFile(null); }}
                className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:underline">
                Remove
              </button>
            </div>
          ) : (
            <label htmlFor="appfolio-csv-file"
              data-testid="csv-file-picker-btn"
              className={`inline-flex items-center gap-2 h-10 px-4 border-2 border-slate-900 bg-white hover:bg-slate-50 text-slate-900 font-black uppercase tracking-widest text-xs cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              {uploading ? <ArrowsClockwise className="animate-spin" size={12}/> : <UploadSimple weight="bold" size={12}/>}
              {uploading ? "Uploading…" : "Choose file from computer"}
            </label>
          )}
          <div className="flex gap-2 flex-wrap pt-2 border-t border-slate-200">
            <button onClick={doPreview} disabled={busy || (!csvUrl.trim() && !stageToken)} data-testid="preview-btn"
              className="h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-xs disabled:opacity-50 inline-flex items-center gap-1">
              {busy ? <ArrowsClockwise className="animate-spin" size={12}/> : <MagnifyingGlass weight="bold" size={12}/>}
              Preview delta <ArrowRight weight="bold" size={12}/>
            </button>
          </div>
        </div>

        {/* Step 2 — cherry-pick */}
        {preview && (
          <div className="space-y-3" data-testid="preview-panel">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <span className="h-5 w-5 inline-flex items-center justify-center bg-cyan-600 text-white text-[10px]">2</span>
              Cherry-pick what to import
            </div>

            {/* Category tabs w/ live counts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {TABS.map(T => {
                const Icon = T.icon;
                const items = preview[T.id] || [];
                const total = items.length;
                const wouldCreate = items.filter(x => x.would_create).length;
                const picked = sel[T.id].size;
                const active = tab === T.id;
                return (
                  <button key={T.id} onClick={() => setTab(T.id)}
                    data-testid={`tab-${T.id}`}
                    className={`p-3 border-2 text-left transition ${active
                      ? "border-[#0f172a] bg-white shadow-[3px_3px_0_#0f172a]"
                      : "border-slate-200 bg-slate-50 hover:border-slate-400"}`}>
                    <div className="flex items-center gap-1.5">
                      <Icon weight="fill" size={14} className={TONE[T.accent].accent}/>
                      <div className="font-black uppercase text-[11px] tracking-widest">{T.label}</div>
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <div className="font-black text-2xl">{picked}</div>
                      <div className="text-[10px] text-slate-500">/ {wouldCreate} new · {total} total</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Search + bulk actions */}
            <div className="rugged-card p-3 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Reviewing: <span className={TONE[TABS.find(x => x.id === tab).accent].accent}>
                    {TABS.find(x => x.id === tab).label}
                  </span>
                </div>
                <input value={query} onChange={e => setQuery(e.target.value)}
                  placeholder="Filter this list…" data-testid="bucket-search"
                  className="h-8 px-2 border-2 border-slate-300 focus:border-cyan-500 text-xs outline-none flex-1 max-w-[220px]"/>
                <button onClick={() => setAllInFiltered(true)} data-testid="select-all"
                  className="h-8 px-2 border-2 border-slate-300 hover:border-emerald-500 text-[10px] font-black uppercase tracking-widest">
                  Select all
                </button>
                <button onClick={() => setAllInFiltered(false)} data-testid="deselect-all"
                  className="h-8 px-2 border-2 border-slate-300 hover:border-red-500 text-[10px] font-black uppercase tracking-widest">
                  None
                </button>
                <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Showing {filtered.length} / {bucket.length}
                </span>
              </div>

              {tab === "jobs"       && <JobsList       items={filtered} sel={sel.jobs}       onToggle={toggle} updateExisting={updateExisting}/>}
              {tab === "properties" && <PropertiesList items={filtered} sel={sel.properties} onToggle={toggle}/>}
              {tab === "vendors"    && <NameList       items={filtered} sel={sel.vendors}    onToggle={toggle} accent="amber"/>}
              {tab === "residents"  && <NameList       items={filtered} sel={sel.residents}  onToggle={toggle} accent="fuchsia"/>}
            </div>

            {/* Sticky apply bar */}
            <div className="sticky bottom-3 z-10 rugged-card p-3 bg-white shadow-[6px_6px_0_#0f172a] border-4 border-[#0f172a]">
              <label className="flex items-start gap-2 pb-2 mb-2 border-b border-slate-200 cursor-pointer" data-testid="update-existing-row">
                <input type="checkbox" checked={updateExisting}
                  onChange={e => setUpdateExisting(e.target.checked)}
                  data-testid="update-existing-checkbox"
                  className="mt-0.5 h-4 w-4 accent-cyan-600"/>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black uppercase tracking-widest">
                    Also sync already-imported WOs
                  </div>
                  <div className="text-[11px] text-slate-500 leading-snug">
                    Refresh <b>status</b>, <b>vendor</b>, <b>schedule</b>, and <b>priority</b> on existing WOs from the CSV. Never overwrites tech notes, photos, or checklists. Tick this when you re-import an updated AppFolio export.
                  </div>
                </div>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xs">
                  <b className="text-emerald-700">{sel.jobs.size}</b> WOs ·
                  <b className="text-cyan-700"> {sel.properties.size}</b> addresses ·
                  <b className="text-amber-700"> {sel.vendors.size}</b> subs ·
                  <b className="text-fuchsia-700"> {sel.residents.size}</b> tenants
                </div>
                <button onClick={doApplySelected} disabled={applying || totalSelected === 0}
                  data-testid="apply-selected-btn"
                  className="ml-auto h-11 px-5 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-40 shadow-[3px_3px_0_#0f172a]">
                  {applying ? <ArrowsClockwise className="animate-spin" size={14}/> : <Check weight="bold" size={14}/>}
                  Import {totalSelected} selected
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History */}
        {runs.length > 0 && (
          <div className="rugged-card p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
              <ClockCounterClockwise weight="bold" size={12}/> Recent imports
            </div>
            <div className="text-xs space-y-1">
              {runs.slice(0, 10).map(r => (
                <div key={r._id} className="flex items-center gap-2 border-b border-slate-100 pb-1 last:border-0 flex-wrap">
                  <span className="text-slate-500 font-mono">{r.ran_at?.slice(0, 16).replace("T", " ")}</span>
                  {r.mode === "selected" && (
                    <span className="text-[9px] font-black uppercase tracking-widest text-cyan-800 bg-cyan-100 px-1">picked</span>
                  )}
                  <span className="text-emerald-700">+{r.created_wos} WOs</span>
                  <span className="text-cyan-700">+{r.created_properties} props</span>
                  {r.created_vendors > 0   && <span className="text-amber-700">+{r.created_vendors} subs</span>}
                  {r.created_residents > 0 && <span className="text-fuchsia-700">+{r.created_residents} tenants</span>}
                  {r.errors?.length > 0    && <span className="text-red-700">{r.errors.length} errors</span>}
                  <span className="text-slate-400 ml-auto truncate max-w-[220px]">by {r.ran_by}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <LockboxLeaseImport/>
    </AdminShell>
  );
}

// ─────────────────────── row components ─────────────────────────
function JobsList({ items, sel, onToggle, updateExisting = false }) {
  if (items.length === 0) return <EmptyRow/>;
  return (
    <div className="overflow-x-auto max-h-[52vh] overflow-y-auto border-2 border-slate-100">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 sticky top-0 z-[1]">
          <tr className="text-left">
            <th className="p-2 w-8"></th>
            <th className="p-2">WO#</th>
            <th className="p-2">Address</th>
            <th className="p-2">Unit</th>
            <th className="p-2">Title</th>
            <th className="p-2">Vendor</th>
            <th className="p-2">Tenant</th>
            <th className="p-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((j, i) => {
            const alreadyImported = !j.would_create && j.wo && j.skip_reason === "already_imported";
            const selectable = !!j.wo && (j.would_create || (updateExisting && alreadyImported));
            const dimmed = !j.would_create && !selectable;
            return (
              <tr key={i}
                className={`border-t border-slate-200 ${dimmed ? "bg-slate-50 text-slate-400" : ""} hover:bg-cyan-50`}>
                <td className="p-2">
                  <input type="checkbox" data-testid={`job-check-${j.wo || i}`}
                    disabled={!selectable}
                    checked={j.wo ? sel.has(j.wo) : false}
                    onChange={() => onToggle(j.wo)}/>
                </td>
                <td className="p-2 font-mono">{j.wo || "—"}</td>
                <td className="p-2 truncate max-w-[220px]" title={j.address}>{j.address}</td>
                <td className="p-2">{j.unit}</td>
                <td className="p-2 truncate max-w-[240px]" title={j.title}>{j.title}</td>
                <td className="p-2 truncate max-w-[140px]" title={j.vendor}>{j.vendor}</td>
                <td className="p-2 truncate max-w-[140px]" title={j.tenant}>{j.tenant}</td>
                <td className="p-2">
                  {j.would_create ? (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5">new</span>
                  ) : alreadyImported && updateExisting ? (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 px-1.5 py-0.5">sync</span>
                  ) : (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-500 px-1.5 py-0.5">exists</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PropertiesList({ items, sel, onToggle }) {
  if (items.length === 0) return <EmptyRow/>;
  return (
    <div className="overflow-x-auto max-h-[52vh] overflow-y-auto border-2 border-slate-100">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 sticky top-0 z-[1]">
          <tr className="text-left">
            <th className="p-2 w-8"></th>
            <th className="p-2">Address</th>
            <th className="p-2">Sample unit</th>
            <th className="p-2">WOs</th>
            <th className="p-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p, i) => (
            <tr key={p.address_key || i}
              className={`border-t border-slate-200 ${p.would_create ? "" : "bg-slate-50 text-slate-400"} hover:bg-cyan-50`}>
              <td className="p-2">
                <input type="checkbox" data-testid={`property-check-${p.address_key || i}`}
                  disabled={!p.would_create}
                  checked={sel.has(p.address_key)}
                  onChange={() => onToggle(p.address_key)}/>
              </td>
              <td className="p-2 truncate max-w-[320px]" title={p.address}>{p.address}</td>
              <td className="p-2">{p.sample_unit}</td>
              <td className="p-2 font-mono">{p.wo_count}</td>
              <td className="p-2">
                {p.would_create
                  ? <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 px-1.5 py-0.5">new</span>
                  : <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-500 px-1.5 py-0.5">exists</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NameList({ items, sel, onToggle, accent }) {
  if (items.length === 0) return <EmptyRow/>;
  const chip = TONE[accent].chip;
  return (
    <div className="overflow-x-auto max-h-[52vh] overflow-y-auto border-2 border-slate-100">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 sticky top-0 z-[1]">
          <tr className="text-left">
            <th className="p-2 w-8"></th>
            <th className="p-2">Name</th>
            <th className="p-2">WOs</th>
            <th className="p-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.name || i}
              className={`border-t border-slate-200 ${it.would_create ? "" : "bg-slate-50 text-slate-400"} hover:bg-cyan-50`}>
              <td className="p-2">
                <input type="checkbox" data-testid={`name-check-${it.name || i}`}
                  disabled={!it.would_create}
                  checked={sel.has(it.name)}
                  onChange={() => onToggle(it.name)}/>
              </td>
              <td className="p-2 truncate max-w-[360px]" title={it.name}>{it.name}</td>
              <td className="p-2 font-mono">{it.wo_count}</td>
              <td className="p-2">
                {it.would_create
                  ? <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 ${chip}`}>new</span>
                  : <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-500 px-1.5 py-0.5">exists</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyRow() {
  return <div className="p-6 text-center text-xs text-slate-500 font-black uppercase tracking-widest">Nothing in this bucket</div>;
}


// Lockbox / Lease import — two separate uploaders. Each one does a dry-run
// first (shows preview + detected columns), then user clicks "Apply" to
// commit. Column auto-detect handles AppFolio header name variations so the
// user doesn't have to re-arrange their CSV.
function LockboxLeaseImport() {
  return (
    <>
      <CsvImporter
        kind="lockboxes"
        title="Lockboxes & Access Codes"
        subtitle="Upload AppFolio Units export with Lockbox / Entry Code / Key Tag columns. Codes auto-appear on every WO for that unit."
        endpoint="/imports/appfolio/lockboxes/upload"
        testIdPrefix="lockbox"
        columnOrder={["address", "unit", "lockbox_code", "entry_code", "entry_notes", "key_tag_location"]}
      />
      <CsvImporter
        kind="leases"
        title="Leases & Rent Roll"
        subtitle="Upload AppFolio Rent Roll / Leases export — tenant name, phone, rent, lease start/end. Powers delinquency + renewal alerts."
        endpoint="/imports/appfolio/leases/upload"
        testIdPrefix="lease"
        columnOrder={["address", "unit", "tenant_name", "monthly_rent", "lease_start", "lease_end"]}
      />
    </>
  );
}

function CsvImporter({ kind, title, subtitle, endpoint, testIdPrefix, columnOrder }) {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (f, dry) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`${endpoint}?dry_run=${dry}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      if (dry) {
        toast.success(`Previewing ${r.data.preview_count} row${r.data.preview_count === 1 ? "" : "s"} — review and click Apply`);
      } else {
        toast.success(`✓ Imported ${r.data.updated} unit${r.data.updated === 1 ? "" : "s"}`);
        setFile(null); setPreview(null);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="rugged-card p-4 mt-4" data-testid={`${testIdPrefix}-import-card`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700">AppFolio Import · {kind}</div>
          <h3 className="font-display font-black text-xl mt-0.5">{title}</h3>
          <p className="text-xs text-slate-600 mt-1 max-w-2xl">{subtitle}</p>
        </div>
        <input type="file" accept=".csv" id={`${testIdPrefix}-file`}
          data-testid={`${testIdPrefix}-file-input`}
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setFile(f);
            await upload(f, true);
            e.target.value = "";
          }}/>
        <label htmlFor={`${testIdPrefix}-file`}
          className="shrink-0 h-10 px-4 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-2 cursor-pointer rounded-md">
          {busy ? "Working…" : (file ? "Pick a different CSV" : "Pick CSV")}
        </label>
      </div>

      {preview && preview.detected_columns && (
        <div className="mt-3 p-3 bg-slate-50 border-2 border-slate-200 rounded">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Detected Columns</div>
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            {Object.entries(preview.detected_columns).map(([k, v]) => (
              <span key={k} className={`px-2 py-0.5 rounded border ${v ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-100 border-slate-300 text-slate-500"}`}>
                <b>{k}</b> → {v || "—"}
              </span>
            ))}
          </div>
          <div className="mt-2 text-xs text-slate-600">
            {preview.preview_count} ready · {preview.skipped_rows} skipped
          </div>
        </div>
      )}

      {preview?.preview?.length > 0 && (
        <div className="mt-3 overflow-x-auto border-2 border-slate-200 rounded max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                {columnOrder.map(k => (
                  <th key={k} className="text-left px-2 py-1.5 font-black uppercase tracking-widest text-[9px] text-slate-600 border-b border-slate-200">
                    {k.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.preview.slice(0, 50).map((row, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                  {columnOrder.map(k => (
                    <td key={k} className="px-2 py-1.5 text-slate-700 truncate max-w-[200px]">
                      {row[k] ?? <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {preview.preview.length > 50 && (
            <div className="p-2 text-center text-[10px] text-slate-500 bg-slate-50 border-t border-slate-200">
              Showing first 50 of {preview.preview.length}
            </div>
          )}
        </div>
      )}

      {preview && preview.dry_run && preview.preview_count > 0 && (
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={() => { setPreview(null); setFile(null); }}
            data-testid={`${testIdPrefix}-cancel`}
            className="h-10 px-3 border-2 border-slate-300 text-slate-700 font-black uppercase text-[10px] tracking-widest rounded">
            Cancel
          </button>
          <button onClick={() => upload(file, false)} disabled={busy}
            data-testid={`${testIdPrefix}-apply`}
            className="h-10 px-5 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white font-black uppercase text-[10px] tracking-widest rounded">
            {busy ? "Applying…" : `Apply · ${preview.preview_count} row${preview.preview_count === 1 ? "" : "s"}`}
          </button>
        </div>
      )}
    </div>
  );
}

